import React, { useState, useEffect } from "react";
import { getDatabase, ref, get } from "firebase/database";
import { app } from "./firebase/config";
import "./AdminPostManagement.css";

const AdminPostManagement = ({ userId }) => {
  const [userPosts, setUserPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showPostModal, setShowPostModal] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(15);
  const [filter, setFilter] = useState("all");
  // Initialize date filters to current day by default
  // Since timestamps in DB are already in UTC+7, we use local time
  const getCurrentDateString = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  const [fromDate, setFromDate] = useState(getCurrentDateString());
  const [toDate, setToDate] = useState(getCurrentDateString());

  // Lấy userId từ props hoặc URL query params
  const effectiveUserId =
    userId || new URLSearchParams(window.location.search).get("userId");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [statsSummary, setStatsSummary] = useState({
    totalPosts: 0,
    postTypes: {},
    avgPostsPerDay: 0,
    postsLast7Days: 0,
    activeUsers: 0,
  });

  // Phân trang
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  // Lấy dữ liệu bài viết từ Firebase
  // Lấy dữ liệu bài viết từ Firebase
  const fetchData = async (forceRefresh = false) => {
    setLoading(true);
    try {
      // Nếu không phải refresh bắt buộc và không có userId cụ thể, thử lấy từ cache
      if (!forceRefresh && !effectiveUserId) {
        const cachedData = localStorage.getItem("admin_posts_cache");
        if (cachedData) {
          try {
            const { posts, timestamp } = JSON.parse(cachedData);
            if (posts && Array.isArray(posts) && posts.length > 0) {
              console.log("Sử dụng dữ liệu bài viết từ cache");
              setUserPosts(posts);
              setFilteredPosts(posts);
              setLastUpdated(timestamp);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Lỗi khi đọc cache:", e);
            localStorage.removeItem("admin_posts_cache");
          }
        }
      }

      const db = getDatabase(app);
      let usersData = {};

      if (effectiveUserId) {
        // Nếu có userId, chỉ tải dữ liệu của user đó
        const userRef = ref(db, `users/${effectiveUserId}`);
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          usersData = { [effectiveUserId]: snapshot.val() };
        }
      } else {
        // OPTIMIZED: Tải user_profiles trước để lấy danh sách user
        // Thay vì tải toàn bộ node "users" (rất nặng), ta tải "user_profiles" (nhẹ hơn)
        const profilesRef = ref(db, "user_profiles");
        const profilesSnapshot = await get(profilesRef);
        const profiles = profilesSnapshot.val() || {};
        
        // Sau đó tải posts cho từng user (song song nhưng có giới hạn)
        const userIds = Object.keys(profiles);
        const batchSize = 10; // Giới hạn số lượng request đồng thời
        
        // Helper để chạy promise theo batch
        const fetchInBatches = async (items, batchSize, fn) => {
          const results = [];
          for (let i = 0; i < items.length; i += batchSize) {
            const batch = items.slice(i, i + batchSize);
            const batchResults = await Promise.all(batch.map(fn));
            results.push(...batchResults);
          }
          return results;
        };

        const postsResults = await fetchInBatches(userIds, batchSize, async (uid) => {
          const postsRef = ref(db, `users/${uid}/posts`);
          const snapshot = await get(postsRef);
          return { uid, posts: snapshot.exists() ? snapshot.val() : null };
        });

        // Ghép dữ liệu lại
        usersData = {};
        postsResults.forEach(({ uid, posts }) => {
          if (profiles[uid]) {
            usersData[uid] = {
              ...profiles[uid],
              posts: posts
            };
          }
        });
      }

      if (usersData) {
        const allPosts = [];
        const postTypeCount = {};

        // Duyệt qua từng user
        Object.entries(usersData).forEach(([userId, userData]) => {
          // Kiểm tra xem user có bài viết không
          if (userData.posts) {
            // Duyệt qua từng loại bài viết
            Object.entries(userData.posts).forEach(([postType, postData]) => {
              // Đếm số lượng bài viết theo loại
              if (!postTypeCount[postType]) {
                postTypeCount[postType] = 0;
              }

              // Kiểm tra xem có links không
              if (postData.links && Array.isArray(postData.links)) {
                postTypeCount[postType] += postData.links.length;

                // Thêm từng bài viết vào mảng allPosts
                postData.links.forEach((link, index) => {
                  // Trích xuất timestamp từ chuỗi link
                  let extractedTimestamp = null;
                  let cleanLink = link;

                  // Kiểm tra nếu link có định dạng timestamp|url
                  if (link.includes("|")) {
                    const parts = link.split("|");
                    extractedTimestamp = parts[0];
                    cleanLink = parts[1];
                  }
                  
                  // Lấy thông tin user an toàn hơn
                  const userProfile = userData.profile || {};
                  const userEmail = userProfile.email || userData.email || "Không có email";
                  const userName = userProfile.displayName || userData.name || userData.displayName || "Không có tên";

                  allPosts.push({
                    id: `${userId}_${postType}_${index}`,
                    userId: userId,
                    userEmail: userEmail,
                    userName: userName,
                    postType: postType,
                    link: cleanLink,
                    rawLink: link,
                    timestamp:
                      extractedTimestamp ||
                      userData.createdAt ||
                      "Không có thông tin",
                  });
                });
              }
            });
          }
        });

        // Sắp xếp bài viết theo thời gian từ mới đến cũ
        const sortedPosts = allPosts.sort((a, b) => {
          // Chuyển timestamp về dạng số để so sánh
          const timeA = a.timestamp
            ? typeof a.timestamp === "string" && !isNaN(Number(a.timestamp))
              ? Number(a.timestamp)
              : new Date(a.timestamp).getTime()
            : 0;
          const timeB = b.timestamp
            ? typeof b.timestamp === "string" && !isNaN(Number(b.timestamp))
              ? Number(b.timestamp)
              : new Date(b.timestamp).getTime()
            : 0;
          return timeB - timeA;
        });

        setUserPosts(sortedPosts);
        setFilteredPosts(sortedPosts);

        // Lưu vào cache nếu đang tải tất cả
        if (!effectiveUserId) {
          const now = new Date().toISOString();
          localStorage.setItem(
            "admin_posts_cache",
            JSON.stringify({ posts: sortedPosts, timestamp: now })
          );
          setLastUpdated(now);
        }
      } else {
        setUserPosts([]);
        setFilteredPosts([]);
      }
    } catch (error) {
      console.error("Lỗi khi tải dữ liệu bài viết:", error);
      setUserPosts([]);
      setFilteredPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [effectiveUserId]); // Thêm effectiveUserId vào dependency

  const handleRefresh = () => {
    fetchData(true);
  };

  // Lọc bài viết theo từ khóa tìm kiếm, loại bài viết và userId
  useEffect(() => {
    // Nếu chưa có dữ liệu, không cần lọc
    if (userPosts.length === 0) return;
    let results = userPosts;

    // Lọc theo userId được truyền từ props hoặc URL
    if (effectiveUserId) {
      results = results.filter((post) => post.userId === effectiveUserId);
    }

    // Lọc theo loại bài viết
    if (filter !== "all") {
      results = results.filter((post) => post.postType === filter);
    }

    // Lọc theo từ khóa tìm kiếm
    if (searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      results = results.filter((post) => {
        return (
          (post.userId && post.userId.toLowerCase().includes(searchLower)) ||
          (post.userName &&
            post.userName.toLowerCase().includes(searchLower)) ||
          (post.userEmail &&
            post.userEmail.toLowerCase().includes(searchLower)) ||
          (post.link && post.link.toLowerCase().includes(searchLower)) ||
          (post.postType && post.postType.toLowerCase().includes(searchLower))
        );
      });
    }

    // Lọc theo thời gian với UTC+7
    if (fromDate || toDate) {
      results = results.filter((post) => {
        if (!post.timestamp) return false;

        // Chuyển timestamp thành Date object
        // Timestamp đã là múi giờ +7 nên không cần điều chỉnh thêm
        const postDate = new Date(
          typeof post.timestamp === "string" && !isNaN(Number(post.timestamp))
            ? Number(post.timestamp)
            : post.timestamp
        );

        if (fromDate && toDate) {
          const from = new Date(fromDate + "T00:00:00");
          const to = new Date(toDate + "T23:59:59.999");
          return postDate >= from && postDate <= to;
        } else if (fromDate) {
          const from = new Date(fromDate + "T00:00:00");
          return postDate >= from;
        } else if (toDate) {
          const to = new Date(toDate + "T23:59:59.999");
          return postDate <= to;
        }
        return true;
      });
    }

    // Cập nhật thống kê
    const now = new Date();
    // Timestamps đã là múi giờ +7 nên dùng now trực tiếp
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const postsLast7Days = results.filter((post) => {
      const postDate = new Date(
        typeof post.timestamp === "string" && !isNaN(Number(post.timestamp))
          ? Number(post.timestamp)
          : post.timestamp
      );
      // Timestamp đã là múi giờ +7 nên không cần điều chỉnh
      return postDate >= sevenDaysAgo;
    }).length;

    const uniqueUsers = new Set(results.map((post) => post.userId)).size;

    // Tính trung bình bài viết mỗi ngày
    const timeSpan =
      fromDate && toDate
        ? (new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24) + 1 // +1 để tính cả ngày cuối
        : 7;
    const avgPostsPerDay = timeSpan
      ? (results.length / timeSpan).toFixed(1)
      : 0;

    // Tính số lượng bài viết theo loại từ danh sách đã lọc
    const filteredPostTypeCount = {};
    results.forEach((post) => {
      if (!filteredPostTypeCount[post.postType]) {
        filteredPostTypeCount[post.postType] = 0;
      }
      filteredPostTypeCount[post.postType]++;
    });

    setStatsSummary({
      totalPosts: results.length,
      postTypes: filteredPostTypeCount,
      postsLast7Days,
      avgPostsPerDay,
      activeUsers: uniqueUsers,
    });

    setFilteredPosts(results);
    setCurrentPage(1);
  }, [searchText, filter, userPosts, fromDate, toDate]);

  // Xử lý hiển thị chi tiết bài viết
  const handlePostClick = (post) => {
    setCurrentPost(post);
    setShowPostModal(true);
  };

  // Xử lý thay đổi trang
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Lấy danh sách các loại bài viết duy nhất
  const getUniquePostTypes = () => {
    const postTypes = userPosts
      .map((post) => post.postType)
      .filter((type) => type !== undefined);

    return ["all", ...new Set(postTypes)];
  };

  // Tạo phân trang
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];

    // Giới hạn số trang hiển thị
    let startPage = Math.max(currentPage - 2, 1);
    let endPage = Math.min(startPage + 4, totalPages);

    if (endPage - startPage < 4) {
      startPage = Math.max(endPage - 4, 1);
    }

    // Nút First
    items.push(
      <button
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
        className={`px-2 md:px-3 py-1 rounded ${
          currentPage === 1
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        }`}
      >
        &laquo;&laquo;
      </button>
    );

    // Nút Prev
    items.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-2 md:px-3 py-1 rounded ${
          currentPage === 1
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        }`}
      >
        &laquo;
      </button>
    );

    // Hiển thị trang đầu và dấu ellipsis nếu cần
    if (startPage > 1) {
      items.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-2 md:px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
        >
          1
        </button>
      );
      if (startPage > 2) {
        items.push(
          <span key="ellipsis1" className="px-2 md:px-3 py-1">
            ...
          </span>
        );
      }
    }

    // Trang hiện tại và các trang xung quanh
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          onClick={() => handlePageChange(number)}
          className={`px-2 md:px-3 py-1 rounded ${
            number === currentPage
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {number}
        </button>
      );
    }

    // Hiển thị trang cuối và dấu ellipsis nếu cần
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="ellipsis2" className="px-2 md:px-3 py-1">
            ...
          </span>
        );
      }
      items.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-2 md:px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
        >
          {totalPages}
        </button>
      );
    }

    // Nút Next
    items.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-2 md:px-3 py-1 rounded ${
          currentPage === totalPages
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        }`}
      >
        &raquo;
      </button>
    );

    // Nút Last
    items.push(
      <button
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
        className={`px-2 md:px-3 py-1 rounded ${
          currentPage === totalPages
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        }`}
      >
        &raquo;&raquo;
      </button>
    );

    return (
      <div className="flex flex-wrap justify-center mt-4 gap-1">{items}</div>
    );
  };

  // Định dạng hiển thị loại bài viết
  const renderPostType = (postType) => {
    let badgeClass = "px-2 py-1 text-xs rounded-full ";
    const typeLower = postType.toLowerCase();

    // Xác định màu sắc dựa trên loại bài viết
    if (typeLower.includes("wordpress")) {
      badgeClass += "bg-blue-600 text-white";
      return <span className={badgeClass}>Wordpress</span>;
    } else if (typeLower.includes("productimages")) {
      badgeClass += "bg-pink-500 text-white";
      return <span className={badgeClass}>Bài viết ảnh SP auto</span>;
    } else if (typeLower.includes("normal")) {
      badgeClass += "bg-blue-500 text-white";
      return <span className={badgeClass}>Bài viết thường</span>;
    } else if (typeLower.includes("auto") && !typeLower.includes("photo")) {
      badgeClass += "bg-green-500 text-white";
      return <span className={badgeClass}>Bài viết tự động</span>;
    } else if (typeLower.includes("photos")) {
      badgeClass += "bg-purple-500 text-white";
      return <span className={badgeClass}>Bài viết ảnh</span>;
    } else if (typeLower.includes("photo") || typeLower.includes("autophoto")) {
      badgeClass += "bg-yellow-500 text-white";
      return <span className={badgeClass}>Bài tự động (Ảnh)</span>;
    } else {
      badgeClass += "bg-gray-500 text-white";
      return <span className={badgeClass}>{postType}</span>;
    }
  };

  // Định dạng thời gian theo múi giờ UTC+7
  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    // Xử lý timestamp dạng số
    let date;
    if (typeof timestamp === "string" && !isNaN(Number(timestamp))) {
      date = new Date(Number(timestamp));
    } else {
      date = new Date(timestamp);
    }

    // Timestamp đã là múi giờ +7 nên không cần điều chỉnh
    return date.toLocaleDateString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Mở liên kết Facebook
  const openFacebookPost = (link) => {
    window.open(link, "_blank");
  };

  // Hiển thị thống kê
  const renderStats = () => {
    // Xác định trạng thái lọc hiện tại
    let filterStatus = "";
    if (fromDate && toDate && fromDate === toDate) {
      const today = getCurrentDateString();
      if (fromDate === today) {
        filterStatus = "Bài viết trong ngày hôm nay";
      } else {
        filterStatus = `Bài viết ngày ${new Date(fromDate).toLocaleDateString(
          "vi-VN"
        )}`;
      }
    } else if (fromDate || toDate) {
      filterStatus = "Trong khoảng thời gian đã chọn";
    } else {
      filterStatus = "Tất cả bài viết";
    }

    // Thêm thông tin về loại bài viết nếu đang lọc
    if (filter !== "all") {
      // Sử dụng hàm renderPostType để lấy tên loại bài viết
      const postTypeElement = renderPostType(filter);
      const postTypeName = postTypeElement.props.children;
      filterStatus += ` - ${postTypeName}`;
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-4 mb-6">
        {/* Tổng số bài viết */}
        <div className="bg-white p-2 md:p-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border-t-4 border-blue-500 col-span-2 lg:col-span-2">
          <h3 className="text-xs md:text-sm font-semibold text-gray-500 uppercase mb-1 md:mb-2">
            Tổng số bài viết
          </h3>
          <p className="text-xl md:text-2xl font-bold text-gray-800 mt-1 md:mt-2">
            {statsSummary.totalPosts}
          </p>
          <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-2">
            {filterStatus}
          </p>
          <p className="text-xs md:text-sm text-gray-600 mt-1 md:mt-2">
            Trung bình {statsSummary.avgPostsPerDay} bài/ngày
          </p>
        </div>

        {/* Thống kê theo loại */}
        {Object.entries(statsSummary.postTypes).map(([type, count]) => {
          const percentage = statsSummary.totalPosts
            ? ((count / statsSummary.totalPosts) * 100).toFixed(1)
            : 0;
          return (
            <div
              key={type}
              className={`bg-white p-2 md:p-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border-t-4 ${(() => {
                const typeLower = type.toLowerCase();
                // Xác định màu sắc dựa trên loại bài viết
                if (typeLower.includes("wordpress")) return "border-blue-600";
                if (typeLower.includes("productimages"))
                  return "border-pink-500";
                if (typeLower.includes("normal")) return "border-blue-500";
                if (typeLower.includes("auto") && !typeLower.includes("photo"))
                  return "border-green-500";
                if (typeLower.includes("photos")) return "border-purple-500";
                if (
                  typeLower.includes("photo") ||
                  typeLower.includes("autophoto")
                )
                  return "border-yellow-500";
                return "border-gray-500";
              })()}`}
            >
              <h3 className="text-xs md:text-sm font-semibold text-gray-500 uppercase mb-1 md:mb-2">
                {(() => {
                  // Chuẩn hóa tên loại bài viết
                  const postTypeMap = {
                    // Các loại bài viết cũ
                    normalpostgenerator: "Bài viết thường",
                    autopostgenerator: "Bài viết tự động (SP)",
                    photospostgenerator: "Bài viết ảnh",
                    autophotogenerator: "Bài tự động (Ảnh)",
                    wordpresspostgenerator: "Wordpress",
                    productimagespostgenerator: "Bài viết ảnh SP auto",

                    // Các loại bài viết mới
                    normalposts: "Bài viết thường",
                    autoposts: "Bài viết tự động",
                    photosposts: "Bài viết ảnh",
                    autophotoposts: "Bài tự động (Ảnh)",
                    wordpressposts: "Wordpress",
                    productimagespostposts: "Bài viết ảnh SP auto",
                  };

                  const typeLower = type.toLowerCase();
                  return postTypeMap[typeLower] || type;
                })()}
              </h3>
              <p className="text-xl md:text-2xl font-bold text-gray-800 mt-1 md:mt-2">
                {count}
              </p>
              <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-2">
                {percentage}% tổng số
              </p>
            </div>
          );
        })}

        {/* Bài viết 7 ngày */}
        <div className="bg-white p-2 md:p-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border-t-4 border-indigo-500">
          <h3 className="text-xs md:text-sm font-semibold text-gray-500 uppercase mb-1 md:mb-2">
            Bài viết mới
          </h3>
          <p className="text-xl md:text-2xl font-bold text-gray-800 mt-1 md:mt-2">
            {statsSummary.postsLast7Days}
          </p>
          <p className="text-[10px] md:text-xs text-gray-500 mt-1">
            7 ngày qua
          </p>
        </div>

        {/* User hoạt động */}
        <div className="bg-white p-2 md:p-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border-t-4 border-pink-500">
          <h3 className="text-xs md:text-sm font-semibold text-gray-500 uppercase mb-1 md:mb-2">
            User hoạt động
          </h3>
          <p className="text-xl md:text-2xl font-bold text-gray-800 mt-1 md:mt-2">
            {statsSummary.activeUsers}
          </p>
          <p className="text-[10px] md:text-xs text-gray-500 mt-1">
            Đã đăng bài
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            Quản lý bài viết {effectiveUserId ? "(User cụ thể)" : ""}
          </h2>
          <div className="flex items-center gap-4">
            {lastUpdated && (
              <span className="text-sm text-gray-500">
                Cập nhật: {new Date(lastUpdated).toLocaleTimeString("vi-VN")}
              </span>
            )}
            <button
              onClick={handleRefresh}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              disabled={loading}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-5 w-5 mr-2 ${loading ? "animate-spin" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {loading ? "Đang tải..." : "Làm mới dữ liệu"}
            </button>
          </div>
        </div>

        {/* Thống kê */}
        {renderStats()}

        {/* Tìm kiếm & Lọc */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6 search-filter-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Tìm kiếm */}
            <div className="search-box">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tìm kiếm
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Tìm kiếm theo người dùng, email, liên kết..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 search-input"
                />
                {searchText && (
                  <button
                    onClick={() => setSearchText("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Thời gian */}
            <div className="date-filter">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Thời gian
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <input
                    id="fromDate"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 date-input"
                    placeholder="Từ ngày"
                  />
                  {fromDate && (
                    <button
                      onClick={() => setFromDate("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <input
                    id="toDate"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 date-input"
                    placeholder="Đến ngày"
                  />
                  {toDate && (
                    <button
                      onClick={() => setToDate("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              {(fromDate || toDate) && (
                <button
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                  }}
                  className="mt-2 px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-xs flex items-center clear-filter-btn"
                >
                  <svg
                    className="h-3 w-3 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Xóa bộ lọc
                </button>
              )}
            </div>

            {/* Loại bài viết */}
            <div className="post-type-filter">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loại bài viết
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none select-input"
                >
                  {getUniquePostTypes().map((type) => (
                    <option key={type} value={type}>
                      {(() => {
                        if (type === "all") return "Tất cả loại";

                        // Chuẩn hóa tên loại bài viết
                        const postTypeMap = {
                          // Các loại bài viết cũ
                          normalpostgenerator: "Bài viết thường",
                          autopostgenerator: "Bài viết tự động (SP)",
                          photospostgenerator: "Bài viết ảnh",
                          autophotogenerator: "Bài tự động (Ảnh)",
                          wordpresspostgenerator: "Wordpress",
                          productimagespostgenerator: "Bài viết ảnh SP auto",

                          // Các loại bài viết mới
                          normalPosts: "Bài viết thường",
                          autoPosts: "Bài viết tự động",
                          photosPosts: "Bài viết ảnh",
                          autophotoPosts: "Bài tự động (Ảnh)",
                          wordpressPosts: "Wordpress",
                          productImagesPostPosts: "Bài viết ảnh SP auto",
                        };

                        return postTypeMap[type] || type;
                      })()}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
              {filter !== "all" && (
                <button
                  onClick={() => setFilter("all")}
                  className="mt-2 px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-xs flex items-center clear-filter-btn"
                >
                  <svg
                    className="h-3 w-3 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Xóa bộ lọc
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bảng danh sách bài viết */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-800">
              Danh sách bài viết
            </h3>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="mt-4 text-gray-600">
                  Đang tải dữ liệu bài viết...
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto -mx-6 md:mx-0">
                  <table className="min-w-full bg-white table-auto md:table-fixed">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600 uppercase text-xs md:text-sm leading-normal">
                        <th className="py-2 px-2 md:py-3 md:px-4 text-left w-1/6">
                          ID
                        </th>
                        {/* <th className="py-3 px-4 text-left">Người dùng</th> */}
                        {/* <th className="py-3 px-4 text-left">Email</th> */}
                        <th className="py-2 px-2 md:py-3 md:px-4 text-left w-1/6">
                          Loại
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-4 text-left w-1/3">
                          Link
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-4 text-left w-1/6">
                          Thời gian
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-4 text-center w-1/12">
                          Hành động
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 text-sm">
                      {currentPosts.length > 0 ? (
                        currentPosts.map((post) => (
                          <tr
                            key={post.id}
                            className="border-b border-gray-200 hover:bg-gray-50"
                          >
                            <td className="py-2 px-2 md:py-3 md:px-4 text-left whitespace-nowrap max-w-[80px] md:max-w-[100px] truncate text-xs md:text-sm">
                              {post.userId}
                            </td>
                            {/* <td className="py-3 px-4 text-left max-w-[120px] truncate">
                              {post.userName}
                            </td> */}
                            {/* <td className="py-3 px-4 text-left max-w-[150px] truncate">
                              {post.userEmail}
                            </td> */}
                            <td className="py-2 px-2 md:py-3 md:px-4 text-left text-xs md:text-sm">
                              {renderPostType(post.postType)}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-4 text-left">
                              <a
                                href={post.link}
                                onClick={(e) => {
                                  e.preventDefault();
                                  openFacebookPost(post.link);
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline truncate block max-w-[100px] md:max-w-[150px] text-xs md:text-sm"
                              >
                                {post.link.slice(0, 20)}...
                              </a>
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-4 text-left whitespace-nowrap text-xs md:text-sm">
                              {formatDate(post.timestamp)}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-4 text-center">
                              <button
                                onClick={() => handlePostClick(post)}
                                className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 md:px-3 rounded-md text-xs transition duration-300"
                              >
                                Chi tiết
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="7"
                            className="py-6 text-center text-gray-500"
                          >
                            Không tìm thấy bài viết nào
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal Chi tiết bài viết */}
      {showPostModal && currentPost && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4">
          <div className="relative bg-white rounded-lg shadow-lg max-w-2xl w-full mx-auto max-h-[90vh] overflow-auto">
            <div className="px-3 md:px-6 py-3 md:py-4 border-b flex justify-between items-center">
              <h3 className="text-base md:text-lg font-medium text-gray-900">
                Chi tiết bài viết
              </h3>
              <button
                onClick={() => setShowPostModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-3 md:p-6 overflow-y-auto">
              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    ID bài viết
                  </label>
                  <p className="bg-gray-50 px-2 md:px-3 py-1 md:py-2 rounded-md text-gray-700 break-all text-xs md:text-sm">
                    {currentPost.id}
                  </p>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    ID người dùng
                  </label>
                  <p className="bg-gray-50 px-2 md:px-3 py-1 md:py-2 rounded-md text-gray-700 break-all text-xs md:text-sm">
                    {currentPost.userId}
                  </p>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Tên người dùng
                  </label>
                  <p className="bg-gray-50 px-2 md:px-3 py-1 md:py-2 rounded-md text-gray-700 text-xs md:text-sm">
                    {currentPost.userName}
                  </p>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <p className="bg-gray-50 px-2 md:px-3 py-1 md:py-2 rounded-md text-gray-700 break-all text-xs md:text-sm">
                    {currentPost.userEmail}
                  </p>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Loại bài viết
                  </label>
                  <div className="bg-gray-50 px-2 md:px-3 py-1 md:py-2 rounded-md text-gray-700">
                    {renderPostType(currentPost.postType)}
                  </div>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Link bài viết
                  </label>
                  <div className="bg-gray-50 px-2 md:px-3 py-1 md:py-2 rounded-md text-gray-700 break-all">
                    <a
                      href={currentPost.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline text-xs md:text-sm"
                    >
                      {currentPost.link}
                    </a>
                    {currentPost.rawLink !== currentPost.link && (
                      <p className="text-[10px] md:text-xs text-gray-500 mt-1">
                        Link gốc: {currentPost.rawLink}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Thời gian
                  </label>
                  <p className="bg-gray-50 px-2 md:px-3 py-1 md:py-2 rounded-md text-gray-700 text-xs md:text-sm">
                    {formatDate(currentPost.timestamp)}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-3 md:px-6 py-3 md:py-4 border-t bg-gray-50 flex flex-wrap justify-end gap-2">
              <a
                href={currentPost.link}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-block"
              >
                Xem trên Facebook
              </a>
              <button
                onClick={() => setShowPostModal(false)}
                className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPostManagement;
